import React, { useState, useEffect } from "react";
import { useFetch } from "../../../../hooks/api";
import { toTitleCase } from "utils/formatText";
import { trellisPalette } from "components/custom/analytics/palettes";
import { inventoryBucketValues } from "modules/pricingv2/pricingTypes";
import { Row, Col, FormCheck } from "react-bootstrap";
import { useAppSelector } from "../../../../redux/hooks";
import Highcharts, { type SeriesSankeyPointOptionsObject } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import Loading from "components/core/blocks/Loading";
import { keepPreviousData } from "@tanstack/react-query";
import { type GroupedStats } from "modules/pricingv2/pricingTypes";
import moment from "moment";

const SankeyChart = ({
  leftDate,
  rightDate,
  marketPlace,
  selectedCategories,
}: {
  leftDate: string;
  rightDate: string;
  marketPlace: string;
  selectedCategories: number[];
}) => {
  const filters = {
    mp: marketPlace,
    left_date: leftDate,
    right_date: rightDate,
    category_ids: selectedCategories.length > 0 ? selectedCategories.join(",") : null,
  };

  const sankeyData = useFetch<SeriesSankeyPointOptionsObject[]>(
    ["inventory_sankey", marketPlace, leftDate, rightDate],
    `/pricing/inventory_sankey`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const nodes = [];
  for (let i = 0; i < inventoryBucketValues.length; i++) {
    const bucket = inventoryBucketValues[i];
    let color = trellisPalette[0];
    if (bucket === "healthy") {
      color = trellisPalette[4]; // good
    }
    if (bucket === "excess") {
      color = trellisPalette[7];
    }
    if (bucket === "overstock") {
      color = trellisPalette[10]; // good
    }
    const nodeLeft = {
      id: `${bucket}_${leftDate}`,
      name: toTitleCase(bucket),
      color: color,
      offset: -30,
    };
    nodes.push(nodeLeft);
    const nodeRight = {
      id: `${bucket}_${rightDate}`,
      name: toTitleCase(bucket),
      color: color,
      offset: -30,
    };
    nodes.push(nodeRight);
  }

  if (sankeyData.isLoading) {
    return <Loading fullPage={false} height={300} />;
  }

  const options: Highcharts.Options = {
    chart: {
      style: {
        fontFamily: "Poppins",
        fontWeight: "300",
        fontSize: "20px",
      },
      height: 600,
    },
    title: {
      text: undefined,
    },
    series: [
      {
        type: "sankey",
        keys: ["from", "to", "weight"],
        nodes: nodes,
        data: sankeyData.data,
      },
    ],
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

const EvolutionTab = ({ inventoryMetrics }: { inventoryMetrics: GroupedStats }) => {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const [leftDate, setLeftDate] = useState<string | null>(null);
  const [rightDate, setRightDate] = useState<string | null>(null);
  const [dates, setDates] = useState<string[]>([]);

  useEffect(() => {
    if (dates.length === 2) {
      //   sort dates using moment
      const sortedDates = dates.sort((a, b) => moment(a).diff(moment(b)));
      setLeftDate(sortedDates[0] ?? null);
      setRightDate(sortedDates[1] ?? null);
    } else {
      setLeftDate(null);
      setRightDate(null);
    }
  }, [dates]);

  return (
    <Row>
      <Col className="mb-4 px-4" xs={12}>
        <div className="fs-4 fw-bold mb-3">Select the dates you would like to compare</div>
      </Col>
      <Col className="mb-4 px-4" xs={2}>
        {Object.keys(inventoryMetrics).map((date) => {
          return (
            <Row key={date}>
              <Col key={date} className="fs-4">
                <FormCheck
                  type="checkbox"
                  id={date}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const d: string[] = [...dates];
                      d.push(date);
                      setDates(d);
                    } else {
                      const d: string[] = [...dates];
                      const index = d.indexOf(date);
                      if (index > -1) {
                        d.splice(index, 1);
                      }
                      setDates(d);
                    }
                  }}
                  label={moment(date).format("MMMM DD YYYY")}
                  disabled={!!(dates.length >= 2 && !dates.includes(date))}
                  checked={dates.includes(date)}
                />
              </Col>
            </Row>
          );
        })}
      </Col>
      <Col className="mb-4 px-4" xs={10}>
        {leftDate && rightDate && (
          <SankeyChart leftDate={leftDate} rightDate={rightDate} marketPlace={marketPlace} selectedCategories={[]} />
        )}
      </Col>
    </Row>
  );
};

export default EvolutionTab;
